import { Check, AlertTriangle, ArrowRight, Database, FileSpreadsheet, ChevronRight, Clock, Package } from "lucide-react";
import { Link } from "react-router-dom";

const fmt = (n) => new Intl.NumberFormat("es-DO").format(Math.round(n || 0));

// Returns { label, tone } describing how stale a data source is.
// tone -> 'green' (fresh ≤7d), 'amber' (8-30d), 'red' (>30d / never)
function freshness(iso) {
  if (!iso) return { label: "Sin cargas registradas", tone: "red", days: null };
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return { label: "Sin cargas registradas", tone: "red", days: null };
  const days = Math.floor((Date.now() - t) / 86400000);
  const human =
    days <= 0 ? "hoy" :
    days === 1 ? "ayer" :
    days < 30 ? `hace ${days}d` :
    days < 365 ? `hace ${Math.round(days / 30)} meses` :
    `hace ${Math.round(days / 365)} años`;
  const tone = days <= 7 ? "green" : days <= 30 ? "amber" : "red";
  return { label: `Actualizado ${human}`, tone, days };
}

const TONE = {
  green: { bg: "#d1fae5", color: "#065f46" },
  amber: { bg: "#fef3c7", color: "#92400e" },
  red:   { bg: "#fee2e2", color: "#991b1b" },
};

/**
 * Explains that the Forecast module needs BOTH leads (from CRM) and sales
 * (from Facturación XLSX) to work. Renders a 2-step checklist with live
 * counts AND freshness alerts so admins know when data is outdated.
 */
export default function ForecastRequirementsCard({ data }) {
  const leadsCount = data?.powerbi_csv?.leads_count || 0;
  const leadsLastAt = data?.powerbi_csv?.last_ingest_at || null;
  const salesCount = data?.sales?.sales_count ?? null;
  const salesLastAt = data?.sales?.last_ingest_at || null;
  const inventoryCount = data?.inventory?.inventory_count ?? null;
  const inventoryAvailable = data?.inventory?.available_count ?? null;
  const inventoryLastAt = data?.inventory?.last_ingest_at || null;

  const hasLeads = leadsCount > 0;
  const hasSales = (salesCount || 0) > 0;
  const hasInventory = (inventoryCount || 0) > 0;
  const bothReady = hasLeads && hasSales;   // core forecast — inventory is a nice-to-have

  const leadsFresh = freshness(leadsLastAt);
  const salesFresh = freshness(salesLastAt);
  const inventoryFresh = freshness(inventoryLastAt);
  // Overall freshness alert: show if ANY core source is stale (amber/red).
  const stale = bothReady && (leadsFresh.tone !== "green" || salesFresh.tone !== "green");

  return (
    <section className="surface overflow-hidden" data-testid="forecast-requirements-card">
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-start gap-3 mb-2">
          <div className="kpi-icon-clean" style={{ color: "#3b82f6", boxShadow: "inset 0 0 0 1px #3b82f61f" }}>
            <AlertTriangle size={18}/>
          </div>
          <div>
            <h3 className="font-display font-extrabold text-lg">Insumos requeridos para Forecast</h3>
            <p className="text-xs text-gray-500 font-semibold mt-0.5">
              El módulo necesita <strong>Leads + Ventas</strong> para calcular conversión y proyectar forecast. El <strong>Inventario</strong> es opcional pero recomendado — permite ajustar el forecast contra el stock realmente disponible.
            </p>
          </div>
        </div>

        {bothReady && !stale && (
          <div className="mt-3 px-3 py-2 rounded-lg text-xs font-bold flex items-center gap-2"
               style={{ background: "#d1fae5", color: "#065f46" }}
               data-testid="forecast-ready">
            <Check size={14}/> Listo · fuentes cargadas y al día. El dashboard de Forecast ya está alimentado.
          </div>
        )}

        {bothReady && stale && (
          <div className="mt-3 px-3 py-2 rounded-lg text-xs font-bold flex items-center gap-2"
               style={{ background: "#fef3c7", color: "#92400e" }}
               data-testid="forecast-stale-warning">
            <Clock size={14}/> Atención · una o más fuentes tienen data antigua. Considera refrescar las cargas para que el forecast refleje la realidad reciente.
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-3 divide-x divide-gray-100">
        <RequirementStep
          step={1}
          icon={<Database size={18}/>}
          title="Leads (CRM)"
          done={hasLeads}
          count={hasLeads ? `${fmt(leadsCount)} leads` : "Sin datos"}
          freshness={hasLeads ? leadsFresh : null}
          desc="Sube el export de Dynamics 365 / PowerBI en la pestaña CRM Overview."
          actionLabel="Ir a CRM Overview"
          actionTab="crm"
        />
        <RequirementStep
          step={2}
          icon={<FileSpreadsheet size={18}/>}
          title="Facturación (Ventas)"
          done={hasSales}
          count={salesCount === null ? "Cargando…" : hasSales ? `${fmt(salesCount)} ventas` : "Sin datos"}
          freshness={hasSales ? salesFresh : null}
          desc="XLSX de facturación (hoja 'Export'). Sube desde la tarjeta de abajo."
          actionLabel={hasSales ? "Ver datos cargados" : "Subir archivo abajo"}
          scrollDown
          scrollTarget="[data-testid='sales-upload-card']"
        />
        <RequirementStep
          step={3}
          icon={<Package size={18}/>}
          title="Inventario (Opcional)"
          optional
          done={hasInventory}
          count={inventoryCount === null ? "Cargando…"
                 : hasInventory ? `${fmt(inventoryCount)} uds · ${fmt(inventoryAvailable || 0)} disp.` : "Sin datos"}
          freshness={hasInventory ? inventoryFresh : null}
          desc="XLSX de inventario (hoja 'Export'). Mejora el forecast contrastando con stock real."
          actionLabel={hasInventory ? "Ver datos cargados" : "Subir archivo abajo"}
          scrollDown
          scrollTarget="[data-testid='inventory-upload-card']"
        />
      </div>

      {bothReady && (
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between flex-wrap gap-3"
             style={{ background: "#eff6ff" }}>
          <span className="text-sm font-bold" style={{ color: "#1e3a8a" }}>
            Todo listo · revisa los KPIs, la serie histórica y el forecast 3m.
          </span>
          <Link
            to="/predictive"
            className="px-4 py-2 rounded-lg font-extrabold text-sm flex items-center gap-2"
            style={{ background: "#111827", color: "#f4c400" }}
            data-testid="forecast-go-dashboard"
          >
            Abrir Dashboard de Forecast <ArrowRight size={14}/>
          </Link>
        </div>
      )}
    </section>
  );
}

function RequirementStep({ step, icon, title, done, count, freshness, desc, actionLabel, actionTab, scrollDown, scrollTarget, optional }) {
  const accent = done ? "#16a34a" : optional ? "#3b82f6" : "#f59e0b";
  const tone = freshness ? TONE[freshness.tone] : null;

  const handle = () => {
    if (actionTab) {
      window.dispatchEvent(new CustomEvent("integrations:set-tab", { detail: actionTab }));
    } else if (scrollDown) {
      const sel = scrollTarget || "[data-testid='sales-upload-card']";
      const el = document.querySelector(sel);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const badgeText = done ? "OK" : optional ? "OPCIONAL" : "PENDIENTE";
  const badgeBg = done ? "#d1fae5" : optional ? "#dbeafe" : "#fef3c7";
  const badgeColor = done ? "#065f46" : optional ? "#1e3a8a" : "#92400e";

  return (
    <div className="p-5 flex flex-col gap-3" data-testid={`forecast-req-step-${step}`}>
      <div className="flex items-start gap-3">
        <div className="flex flex-col items-center gap-1">
          <div className="w-9 h-9 rounded-full flex items-center justify-center font-display font-extrabold text-sm"
               style={{ background: done ? "#d1fae5" : optional ? "#dbeafe" : "#fef3c7",
                        color: done ? "#065f46" : optional ? "#1e3a8a" : "#92400e",
                        border: `2px solid ${accent}` }}>
            {done ? <Check size={16}/> : step}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span style={{ color: accent }}>{icon}</span>
            <h4 className="font-display font-extrabold">{title}</h4>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-extrabold"
                  style={{ background: badgeBg, color: badgeColor }}>
              {badgeText}
            </span>
            {freshness && tone && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold"
                    style={{ background: tone.bg, color: tone.color }}
                    data-testid={`forecast-req-freshness-${step}`}>
                <Clock size={10}/> {freshness.label}
              </span>
            )}
          </div>
          <div className="font-display font-extrabold text-xl" style={{ color: done ? "#111827" : "#92400e" }}>
            {count}
          </div>
          <p className="text-xs text-gray-500 font-semibold mt-1">{desc}</p>
        </div>
      </div>
      <button
        onClick={handle}
        className="self-start text-xs font-extrabold flex items-center gap-1 hover:underline"
        style={{ color: done ? "#475569" : optional ? "#0ea5e9" : "#0ea5e9" }}
        data-testid={`forecast-req-action-${step}`}
      >
        {actionLabel} <ChevronRight size={12}/>
      </button>
    </div>
  );
}
